import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import ToastContainer from '../../components/ToastContainer';
import { apiFetch } from '../../utils/apiClient';
import { formatDate } from '../../utils/format';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { useVendor } from '../../contexts/VendorContext';

/**
 * Seller onboarding form. The sales rep fills this in on the seller's behalf
 * (new seller → account created as pending; existing seller → details
 * completed). Approval is gated on the required fields.
 */

interface FormState {
  email: string;
  store_name: string;
  first_name: string;
  last_name: string;
  preferred_language: string;
  business_name: string;
  store_type: string;
  business_registration_number: string;
  years_in_operation: string;
  estimated_books: string;
  owner_name: string;
  nic_number: string;
  date_of_birth: string;
  contact_phone: string;
  home_address: string;
  address: string;
  city: string;
  bank_name: string;
  bank_branch: string;
  bank_account_name: string;
  bank_account_number: string;
  commission_rate: string;
  notes: string;
  terms_accepted: boolean;
}

const EMPTY: FormState = {
  email: '', store_name: '', first_name: '', last_name: '', preferred_language: 'en',
  business_name: '', store_type: '', business_registration_number: '', years_in_operation: '', estimated_books: '',
  owner_name: '', nic_number: '', date_of_birth: '', contact_phone: '', home_address: '', address: '', city: '',
  bank_name: '', bank_branch: '', bank_account_name: '', bank_account_number: '', commission_rate: '', notes: '',
  terms_accepted: false,
};

interface VendorRecord extends Partial<Record<keyof FormState, any>> {
  id: number;
  name: string;
  contact_email?: string | null;
  username: string;
  is_active: boolean;
  approved_at: string | null;
  onboarding_missing: string[];
  onboarding_complete: boolean;
  onboarded_by_name: string | null;
  terms_accepted_at: string | null;
  onboarding_completed_at: string | null;
}

interface ToastData { id: string; message: string; type: 'success' | 'error' | 'info' | 'warning' }

const STORE_TYPES = ['individual', 'bookshop', 'publisher', 'other'];
const inputClass = 'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base sm:text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500';

const Field: React.FC<{ label: string; required?: boolean; hint?: string; className?: string; children: React.ReactNode }> = ({ label, required, hint, className = '', children }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <section className="bg-white shadow rounded-lg p-4 sm:p-5">
    <h2 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">{children}</div>
  </section>
);

const AdminVendorOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useVendor();
  const isNew = !id;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [vendor, setVendor] = useState<VendorRecord | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [credential, setCredential] = useState<{ username: string; password: string; store: string } | null>(null);
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = (message: string, type: ToastData['type']) =>
    setToasts((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, message, type }]);

  const applyVendor = useCallback((v: VendorRecord) => {
    setVendor(v);
    setForm({
      email: v.email || v.contact_email || '',
      store_name: v.name || '',
      first_name: v.first_name || '',
      last_name: v.last_name || '',
      preferred_language: v.preferred_language || 'en',
      business_name: v.business_name || '',
      store_type: v.store_type || '',
      business_registration_number: v.business_registration_number || '',
      years_in_operation: v.years_in_operation == null ? '' : String(v.years_in_operation),
      estimated_books: v.estimated_books == null ? '' : String(v.estimated_books),
      owner_name: v.owner_name || '',
      nic_number: v.nic_number || '',
      date_of_birth: v.date_of_birth || '',
      contact_phone: v.contact_phone || '',
      home_address: v.home_address || '',
      address: v.address || '',
      city: v.city || '',
      bank_name: v.bank_name || '',
      bank_branch: v.bank_branch || '',
      bank_account_name: v.bank_account_name || '',
      bank_account_number: v.bank_account_number || '',
      commission_rate: v.commission_rate == null ? '' : String(v.commission_rate),
      notes: v.notes || '',
      terms_accepted: !!v.terms_accepted,
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const r = await apiFetch(`/api/admin/vendors/${id}/`);
        if (!r.ok) throw new Error(t('common.genericError'));
        applyVendor(await r.json());
      } catch (err: any) {
        setError(err.message || t('common.genericError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, applyVendor, t]);

  const set = (key: keyof FormState, value: string | boolean) => setForm((f) => ({ ...f, [key]: value }));

  const payload = () => {
    const num = (v: string) => (v.trim() === '' ? null : Number(v));
    const str = (v: string) => v.trim();
    const base: any = {
      first_name: str(form.first_name),
      last_name: str(form.last_name),
      preferred_language: form.preferred_language,
      business_name: str(form.business_name),
      store_type: form.store_type,
      business_registration_number: str(form.business_registration_number),
      years_in_operation: num(form.years_in_operation),
      estimated_books: num(form.estimated_books),
      owner_name: str(form.owner_name),
      nic_number: str(form.nic_number),
      date_of_birth: form.date_of_birth || null,
      contact_phone: str(form.contact_phone),
      home_address: str(form.home_address),
      address: str(form.address),
      city: str(form.city),
      bank_name: str(form.bank_name),
      bank_branch: str(form.bank_branch),
      bank_account_name: str(form.bank_account_name),
      bank_account_number: str(form.bank_account_number),
      commission_rate: form.commission_rate.trim() === '' ? null : form.commission_rate,
      notes: str(form.notes),
      terms_accepted: form.terms_accepted,
    };
    if (isNew) {
      base.email = str(form.email).toLowerCase();
      base.store_name = str(form.store_name);
    } else {
      base.name = str(form.store_name);
    }
    return base;
  };

  const handleErrors = async (r: Response) => {
    const data = await r.json().catch(() => ({}));
    if (data.error) throw new Error(String(data.error));
    const errs: Record<string, string> = {};
    Object.entries(data).forEach(([k, v]) => { errs[k] = Array.isArray(v) ? String(v[0]) : String(v); });
    setFieldErrors(errs);
    const first = Object.values(errs)[0];
    throw new Error(first || t('common.genericError'));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSaving(true);
    try {
      const r = await apiFetch(isNew ? '/api/admin/vendors/onboard/' : `/api/admin/vendors/${id}/`, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload()),
      });
      if (!r.ok) await handleErrors(r);
      const data: VendorRecord & { temporary_password?: string; invite_sent?: boolean } = await r.json();
      if (isNew) {
        if (data.temporary_password) {
          setCredential({ username: data.username, password: data.temporary_password, store: data.name });
          applyVendor(data);
          navigate(`/admin/vendors/${data.id}`, { replace: true });
        } else {
          addToast(t('admin.onb.createdInvited', { email: form.email }), 'success');
          navigate(`/admin/vendors/${data.id}`, { replace: true });
        }
      } else {
        applyVendor(data);
        addToast(data.onboarding_complete ? t('admin.onb.savedComplete') : t('admin.onb.savedIncomplete', { count: data.onboarding_missing.length }), data.onboarding_complete ? 'success' : 'info');
      }
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!vendor) return;
    setApproving(true);
    setError('');
    try {
      const r = await apiFetch(`/api/admin/vendors/${vendor.id}/approve/`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || t('common.genericError'));
      applyVendor(data);
      addToast(t('admin.v.approved', { name: vendor.name }), 'success');
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
    } finally {
      setApproving(false);
    }
  };

  const missing = vendor?.onboarding_missing || [];
  const pending = !!vendor && !vendor.is_active && !vendor.approved_at;

  return (
    <Layout>
      <ToastContainer toasts={toasts} onRemove={(tid) => setToasts((p) => p.filter((x) => x.id !== tid))} />
      {credential && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.v.credentialTitle', { store: credential.store })}</h2>
            <p className="text-sm text-gray-600">{t('admin.onb.credentialBody')}</p>
            <dl className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
              <div className="flex justify-between gap-3"><dt className="text-gray-500">{t('signup.username')}</dt><dd className="font-mono font-medium select-all">{credential.username}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-gray-500">{t('auth.password')}</dt><dd className="font-mono font-medium select-all">{credential.password}</dd></div>
            </dl>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { navigator.clipboard?.writeText(`${t('signup.username')}: ${credential.username}\n${t('auth.password')}: ${credential.password}`).catch(() => undefined); }}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm min-h-[40px]"
              >
                {t('admin.v.copy')}
              </button>
              <button onClick={() => setCredential(null)} className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[40px]">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-3xl mx-auto">
        <Link to="/admin/vendors" className="text-sm text-blue-600 hover:underline">&larr; {t('admin.v.title')}</Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mt-2 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{isNew ? t('admin.onb.titleNew') : t('admin.onb.titleEdit', { store: vendor?.name || '' })}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('admin.onb.intro')}</p>
          </div>
          {vendor && (
            <div className="flex-shrink-0">
              {vendor.is_active ? (
                <span className="inline-flex items-center rounded-full bg-green-100 text-green-800 px-3 py-1 text-xs font-semibold">{t('admin.v.active')}</span>
              ) : vendor.onboarding_complete ? (
                <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-xs font-semibold">{t('admin.onb.readyToApprove')}</span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-semibold">{t('admin.onb.incomplete', { count: missing.length })}</span>
              )}
            </div>
          )}
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800 mb-4">{error}</div>}

        {loading ? (
          <div className="text-center py-12"><div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {vendor && !vendor.is_active && missing.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <p className="font-medium">{t('admin.onb.missingTitle')}</p>
                <p className="mt-0.5">{missing.join(' · ')}</p>
              </div>
            )}

            <Section title={t('admin.onb.account')} subtitle={t('admin.onb.accountHint')}>
              <Field label={t('signup.storeName')} required>
                <input required className={inputClass} value={form.store_name} onChange={(e) => set('store_name', e.target.value)} />
                {fieldErrors.store_name && <p className="text-xs text-red-600 mt-1">{fieldErrors.store_name}</p>}
                {fieldErrors.name && <p className="text-xs text-red-600 mt-1">{fieldErrors.name}</p>}
              </Field>
              <Field label={t('signup.email')} required={isNew} hint={!isNew ? t('admin.onb.emailLocked') : undefined}>
                <input required={isNew} type="email" disabled={!isNew} className={`${inputClass} disabled:bg-gray-100 disabled:text-gray-500`} value={form.email} onChange={(e) => set('email', e.target.value)} />
                {fieldErrors.email && <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>}
              </Field>
              <Field label={t('signup.firstName')}>
                <input className={inputClass} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
              </Field>
              <Field label={t('signup.lastName')}>
                <input className={inputClass} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
              </Field>
              <Field label={t('profile.preferredLanguage', 'Preferred language')}>
                <select className={inputClass} value={form.preferred_language} onChange={(e) => set('preferred_language', e.target.value)}>
                  {SUPPORTED_LANGUAGES.map((l) => <option key={l} value={l}>{t(`language.${l}`)}</option>)}
                </select>
              </Field>
              <Field label={t('admin.v.commission')} hint={t('admin.onb.commissionHint')}>
                <input type="number" step="0.01" min="0" max="100" className={inputClass} value={form.commission_rate} onChange={(e) => set('commission_rate', e.target.value)} />
              </Field>
            </Section>

            <Section title={t('admin.onb.owner')} subtitle={t('admin.onb.ownerHint')}>
              <Field label={t('admin.onb.ownerName')} required>
                <input className={inputClass} value={form.owner_name} onChange={(e) => set('owner_name', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.nic')} required hint={t('admin.onb.nicHint')}>
                <input className={inputClass} value={form.nic_number} onChange={(e) => set('nic_number', e.target.value)} />
                {fieldErrors.nic_number && <p className="text-xs text-red-600 mt-1">{fieldErrors.nic_number}</p>}
              </Field>
              <Field label={t('admin.onb.dob')}>
                <input type="date" className={inputClass} value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
              </Field>
              <Field label={t('signup.phone')} required>
                <input type="tel" className={inputClass} value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.homeAddress')} required className="sm:col-span-2">
                <textarea rows={2} className={inputClass} value={form.home_address} onChange={(e) => set('home_address', e.target.value)} />
              </Field>
              <Field label={t('signup.city')} required>
                <input className={inputClass} value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Field>
            </Section>

            <Section title={t('admin.onb.business')} subtitle={t('admin.onb.businessHint')}>
              <Field label={t('admin.onb.storeType')} required>
                <select className={inputClass} value={form.store_type} onChange={(e) => set('store_type', e.target.value)}>
                  <option value="">—</option>
                  {STORE_TYPES.map((s) => <option key={s} value={s}>{t(`admin.onb.storeTypes.${s}`)}</option>)}
                </select>
              </Field>
              <Field label={t('admin.onb.businessName')}>
                <input className={inputClass} value={form.business_name} onChange={(e) => set('business_name', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.regNumber')} hint={t('admin.onb.regNumberHint')}>
                <input className={inputClass} value={form.business_registration_number} onChange={(e) => set('business_registration_number', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.storeAddress')} hint={t('admin.onb.storeAddressHint')}>
                <input className={inputClass} value={form.address} onChange={(e) => set('address', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.years')}>
                <input type="number" min="0" className={inputClass} value={form.years_in_operation} onChange={(e) => set('years_in_operation', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.estimatedBooks')}>
                <input type="number" min="0" className={inputClass} value={form.estimated_books} onChange={(e) => set('estimated_books', e.target.value)} />
              </Field>
            </Section>

            <Section title={t('admin.onb.bank')} subtitle={t('admin.onb.bankHint')}>
              <Field label={t('admin.onb.bankName')} required>
                <input className={inputClass} value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.bankBranch')} required>
                <input className={inputClass} value={form.bank_branch} onChange={(e) => set('bank_branch', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.accountName')} required>
                <input className={inputClass} value={form.bank_account_name} onChange={(e) => set('bank_account_name', e.target.value)} />
              </Field>
              <Field label={t('admin.onb.accountNumber')} required>
                <input inputMode="numeric" className={inputClass} value={form.bank_account_number} onChange={(e) => set('bank_account_number', e.target.value)} />
              </Field>
            </Section>

            <section className="bg-white shadow rounded-lg p-4 sm:p-5 space-y-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('admin.onb.terms')}</h2>
              <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
                <input type="checkbox" className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={form.terms_accepted} onChange={(e) => set('terms_accepted', e.target.checked)} />
                <span>{t('admin.onb.termsLabel')} <span className="text-red-500">*</span></span>
              </label>
              {vendor?.terms_accepted_at && (
                <p className="text-xs text-gray-500">{t('admin.onb.termsAcceptedOn', { date: formatDate(vendor.terms_accepted_at) })}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('item.notes')}</label>
                <textarea rows={2} className={inputClass} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder={t('admin.onb.notesPlaceholder')} />
              </div>
              {vendor && (vendor.onboarded_by_name || vendor.onboarding_completed_at) && (
                <p className="text-xs text-gray-500">
                  {vendor.onboarded_by_name && t('admin.onb.onboardedBy', { name: vendor.onboarded_by_name })}
                  {vendor.onboarding_completed_at && <> · {t('admin.onb.completedOn', { date: formatDate(vendor.onboarding_completed_at) })}</>}
                </p>
              )}
            </section>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-6">
              <p className="text-xs text-gray-500">{t('admin.onb.requiredNote')}</p>
              <div className="flex gap-2 justify-end">
                <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-md bg-blue-600 text-white text-sm font-semibold min-h-[44px] disabled:opacity-50">
                  {saving ? t('common.saving') : isNew ? t('admin.onb.create') : t('common.save')}
                </button>
                {pending && (
                  <button
                    type="button"
                    onClick={approve}
                    disabled={approving || !vendor?.onboarding_complete}
                    title={!vendor?.onboarding_complete ? t('admin.onb.approveBlocked') : undefined}
                    className="px-5 py-2.5 rounded-md bg-green-600 text-white text-sm font-semibold min-h-[44px] disabled:opacity-50"
                  >
                    {approving ? t('common.saving') : t('admin.v.approve')}
                  </button>
                )}
              </div>
            </div>
            {pending && !vendor?.onboarding_complete && (
              <p className="text-xs text-gray-500 -mt-4 pb-6 text-right">{t('admin.onb.approveBlocked')}</p>
            )}
            {!settings.email_enabled && isNew && (
              <p className="text-xs text-gray-500 -mt-2 pb-6">{t('admin.onb.noEmailNote')}</p>
            )}
          </form>
        )}
      </div>
    </Layout>
  );
};

export default AdminVendorOnboarding;
